import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Logo from '../components/Logo';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Cricket Refreshed — landing page (v3, post-platform rewrite).
 *
 * Sells the new platform: a cricket development OS for families.
 * Lead with the headline product (Coach Twin + daily pro-clip drop +
 * household model), not the v1 ESPN-Cricinfo positioning. Every
 * section ties back to a real surface in the app so a curious visitor
 * can see exactly what they're buying.
 *
 * Section order, top to bottom:
 *   1. Sticky nav with the new logo + auth-aware CTAs.
 *   2. Hero with the real product thesis + two role-targeted CTAs.
 *   3. Three-pillar product strip — Coach Twin, Pro Clips, Family OS.
 *   4. "How it works" four-step strip (onboarding → AI in two minutes).
 *   5. Live demo grid — links into authenticated surfaces with
 *      explanatory copy so visitors can preview without signing up.
 *   6. Coach hook block — SMS roster + WhatsApp blast pitch.
 *   7. Pricing teaser — credit-first, Free is genuinely useful.
 *   8. Social proof + FAQ.
 *   9. Footer with app-store placeholders.
 *
 * No live-stats counters (we won't show "0 athletes trained" on day 1).
 * No newsletter — we have the digest worker for that, and it's
 * household-scoped so it doesn't belong on the public marketing page.
 * No AI-search hero — search is a tiny slice now; the home shouldn't
 * frame the product as a search engine.
 */
export default function HomePage() {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { isAuthenticated, user } = useAuth();
  const [pricing, setPricing] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/credits/pricing`)
      .then((r) => setPricing(r.data))
      .catch(() => setPricing(null));
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const dark = theme === 'dark';
  const goStart = () => navigate(isAuthenticated ? '/dashboard' : '/onboarding');
  const goCoach = () => navigate(isAuthenticated ? '/coach-dashboard' : '/onboarding');

  return (
    <div
      className="min-h-screen"
      style={{
        background: dark
          ? 'radial-gradient(circle at 10% -10%, #0e2438 0%, #0a0e27 35%, #060818 100%)'
          : 'radial-gradient(circle at 10% -10%, #e9fbf3 0%, #f8fafc 40%, #eef2f7 100%)',
        color: dark ? '#f7fafc' : '#0f172a',
      }}
    >
      {/* ===== nav ===== */}
      <nav
        className="sticky top-0 z-40 backdrop-blur-md"
        style={{
          background: scrolled
            ? (dark ? 'rgba(6,8,24,0.85)' : 'rgba(255,255,255,0.85)')
            : 'transparent',
          borderBottom: scrolled
            ? `1px solid ${dark ? 'rgba(0,201,167,0.15)' : 'rgba(15,23,42,0.08)'}`
            : '1px solid transparent',
          transition: 'background 0.18s, border-color 0.18s',
        }}
      >
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-4">
          <button
            onClick={() => navigate('/')}
            className="flex items-center"
            aria-label="Cricket Refreshed home"
          >
            <Logo size={32} />
          </button>
          <div className="hidden md:flex items-center gap-5 ml-4 text-sm">
            <a href="#product" className="opacity-80 hover:opacity-100">Product</a>
            <a href="#coaches" className="opacity-80 hover:opacity-100">For coaches</a>
            <a href="#pricing-teaser" className="opacity-80 hover:opacity-100">Pricing</a>
            <button onClick={() => navigate('/clips')} className="opacity-80 hover:opacity-100">Pro clips</button>
          </div>
          <div className="flex-1" />
          <button
            onClick={toggleTheme}
            className="p-2 rounded-lg"
            style={{ background: dark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.05)' }}
            aria-label="Toggle theme"
          >
            {dark ? '☀' : '☾'}
          </button>
          {isAuthenticated ? (
            <button
              onClick={() => navigate('/dashboard')}
              className="px-3.5 py-2 rounded-lg text-sm font-semibold text-white"
              style={{ background: 'linear-gradient(135deg, #00A651, #00D9FF)' }}
            >
              Open dashboard
            </button>
          ) : (
            <button
              onClick={goStart}
              className="px-3.5 py-2 rounded-lg text-sm font-semibold text-white"
              style={{ background: 'linear-gradient(135deg, #00A651, #00D9FF)' }}
            >
              Get started
            </button>
          )}
          <button
            onClick={() => setMobileNavOpen((v) => !v)}
            className="md:hidden p-2"
            aria-label="Menu"
          >
            ☰
          </button>
        </div>
        {mobileNavOpen && (
          <div className="md:hidden border-t" style={{ borderColor: 'rgba(150,160,180,0.15)' }}>
            <div className="max-w-6xl mx-auto px-4 py-3 grid gap-3 text-sm">
              <a href="#product" onClick={() => setMobileNavOpen(false)}>Product</a>
              <a href="#coaches" onClick={() => setMobileNavOpen(false)}>For coaches</a>
              <a href="#pricing-teaser" onClick={() => setMobileNavOpen(false)}>Pricing</a>
              <button onClick={() => { setMobileNavOpen(false); navigate('/clips'); }} className="text-left">Pro clips</button>
            </div>
          </div>
        )}
      </nav>

      {/* ===== hero ===== */}
      <header className="relative overflow-hidden">
        <BackgroundOrbs dark={dark} />
        <div className="max-w-6xl mx-auto px-4 pt-12 pb-16 md:pt-20 md:pb-24 relative">
          <Eyebrow>For athletes · parents · coaches</Eyebrow>
          <h1
            className="font-extrabold tracking-tight mt-4"
            style={{
              fontSize: 'clamp(2.4rem, 5.6vw, 4.8rem)',
              lineHeight: 1.02,
              letterSpacing: '-0.025em',
            }}
          >
            The cricket development OS<br />
            <span style={{
              background: 'linear-gradient(90deg, #00A651, #00C9A7, #00D9FF)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}>
              for families.
            </span>
          </h1>
          <p className="text-lg md:text-xl mt-5 max-w-2xl opacity-80">
            A streaming AI coach, a daily pro-clip drop, video pose analysis,
            and a household dashboard a parent can actually use. Free starts
            useful — 50 insight credits a month, every month.
          </p>

          <div className="flex flex-wrap gap-3 mt-7">
            <button
              onClick={goStart}
              className="px-5 py-3 rounded-xl font-semibold text-white text-base"
              style={{
                background: 'linear-gradient(135deg, #00A651, #00D9FF)',
                boxShadow: '0 12px 28px -12px rgba(0,201,167,0.5)',
              }}
            >
              Get started — free
            </button>
            <button
              onClick={goCoach}
              className="px-5 py-3 rounded-xl font-semibold text-base"
              style={{
                background: dark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.05)',
                border: `1px solid ${dark ? 'rgba(0,201,167,0.3)' : 'rgba(0,166,81,0.3)'}`,
                color: dark ? '#00C9A7' : '#00875A',
              }}
            >
              I coach a team →
            </button>
          </div>

          <div className="flex flex-wrap gap-4 mt-6 text-xs opacity-70">
            <span>📱 Phone-OTP signup — no passwords</span>
            <span>👨‍👩‍👧 Two parents · multiple athletes per family</span>
            <span>🔒 Cricket-only AI (topic-locked)</span>
          </div>
        </div>
      </header>

      {/* ===== three-pillar product strip ===== */}
      <section id="product" className="max-w-6xl mx-auto px-4 py-14 md:py-20">
        <SectionLabel>What's inside</SectionLabel>
        <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight mt-3">
          Three loops that compound.
        </h2>
        <p className="opacity-70 max-w-2xl mt-2">
          Each loop is real software in your hands the moment you sign up. No
          gated demo, no "request access."
        </p>

        <div className="grid md:grid-cols-3 gap-4 mt-8">
          <PillarCard
            dark={dark}
            tone="#00C9A7"
            kicker="Daily habit"
            title="Today's clip drop"
            body={
              <>
                Every day at 5pm local time, your athlete gets a 15–30s pro
                cricket clip picked for their role, age, and the drill they're
                practicing — with a 30-second AI breakdown that reads like a
                coaching lesson.
              </>
            }
            footer="200+ curated clips · matched to your athlete · pose-imitation drill links."
            illo={<IlloClip dark={dark} />}
            cta="Browse the library"
            onCta={() => navigate('/clips')}
          />

          <PillarCard
            dark={dark}
            tone="#00D9FF"
            kicker="Live coaching"
            title="Coach Twin chat"
            body={
              <>
                A streaming AI coach scoped to your athlete. Sees the last 14
                days of sessions, active goals, ACWR training-load score, plus
                long-term observations refreshed every six turns. Topic-locked
                to cricket and athletic performance.
              </>
            }
            footer="1 credit per message · streaming responses · unlimited history."
            illo={<IlloChat dark={dark} />}
            featured
          />

          <PillarCard
            dark={dark}
            tone="#00A651"
            kicker="Family OS"
            title="Multi-athlete dashboard"
            body={
              <>
                Both parents see the same dashboard. Switch between kids in one
                tap. Saturday family digest lands in your inbox with a weekly
                recap, load monitor, and a notable-session card you can blast
                to the WhatsApp team chat.
              </>
            }
            footer="Up to 4 athletes · invite a coach by phone · auto-link existing CricClubs/CricHeroes/str8bat."
            illo={<IlloFamily dark={dark} />}
          />
        </div>
      </section>

      {/* ===== how it works ===== */}
      <section className="max-w-6xl mx-auto px-4 py-14 md:py-20">
        <SectionLabel>How it works</SectionLabel>
        <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight mt-3">
          From phone to AI feedback, in two minutes.
        </h2>
        <div className="grid md:grid-cols-4 gap-4 mt-8">
          {[
            { n: '1', t: 'Tap your number', b: 'We text a 6-digit code. No password to remember, no email needed.' },
            { n: '2', t: 'Add your athlete', b: 'Name, role, age. Optionally link CricClubs, CricHeroes, or str8bat IDs to auto-import match data.' },
            { n: '3', t: 'Record a session', b: 'Drop a clip. Pose overlay computes elbow / rotation / balance angles — entirely in-browser.' },
            { n: '4', t: 'AI feedback lands', b: 'A 30-second breakdown explains what worked, what to fix, and which drill to try tonight.' },
          ].map((s) => (
            <div
              key={s.n}
              className="rounded-2xl p-5"
              style={{
                background: dark ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.85)',
                border: `1px solid ${dark ? 'rgba(0,201,167,0.18)' : 'rgba(0,166,81,0.18)'}`,
              }}
            >
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center font-extrabold text-white text-sm mb-3"
                style={{ background: 'linear-gradient(135deg, #00A651, #00D9FF)' }}
              >
                {s.n}
              </div>
              <h3 className="font-bold leading-tight">{s.t}</h3>
              <p className="text-sm opacity-75 mt-2 leading-relaxed">{s.b}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ===== live preview links ===== */}
      <section className="max-w-6xl mx-auto px-4 py-14 md:py-20">
        <SectionLabel>See it for yourself</SectionLabel>
        <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight mt-3">
          Real surfaces. Open the ones you're curious about.
        </h2>
        <div className="grid md:grid-cols-3 gap-3 mt-8">
          {[
            ['Pro clip library', 'Curated clips with AI technique breakdowns. Free to browse.', '/clips', '✨'],
            ['Pricing', '50 free credits/month. Subscription tiers. One-time packs.', '/pricing', '⚡'],
            ['7-day practice plan', 'Adaptive plan from your sessions + goals + ACWR load.', '/practice-plan', '🗺️'],
            ['Skill radar + cohort', '6-axis biomechanics radar vs anonymized peer cohort.', '/dashboard', '📊'],
            ['Drill library', 'Public drills + coach-private drills + task assignment.', '/drills', '🏏'],
            ['Coach dashboard', 'Roster leaderboard, SMS funnel analytics, WhatsApp blast.', '/coach-dashboard', '💬'],
          ].map(([title, body, to, icon]) => (
            <button
              key={to}
              onClick={() => navigate(to)}
              className="text-left rounded-2xl p-5 transition-transform hover:-translate-y-0.5"
              style={{
                background: dark ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.9)',
                border: `1px solid ${dark ? 'rgba(150,160,180,0.18)' : 'rgba(15,23,42,0.08)'}`,
              }}
            >
              <div className="text-2xl mb-2">{icon}</div>
              <div className="font-bold">{title}</div>
              <p className="text-sm opacity-70 mt-1.5 leading-relaxed">{body}</p>
              <div className="text-xs font-semibold mt-3" style={{ color: '#00C9A7' }}>
                Open →
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* ===== coach hook ===== */}
      <section
        id="coaches"
        className="max-w-6xl mx-auto px-4 py-14 md:py-20"
      >
        <div
          className="rounded-3xl p-8 md:p-12 relative overflow-hidden"
          style={{
            background: dark
              ? 'linear-gradient(135deg, rgba(0,166,81,0.15), rgba(0,217,255,0.10))'
              : 'linear-gradient(135deg, rgba(0,166,81,0.10), rgba(0,217,255,0.08))',
            border: '1px solid rgba(0,201,167,0.3)',
          }}
        >
          <Eyebrow tone="#00C9A7">For coaches</Eyebrow>
          <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight mt-3 max-w-3xl">
            Invite ten parents in one text. They're on your roster automatically.
          </h2>
          <p className="opacity-80 max-w-3xl mt-3">
            Type a list of phone numbers, hit send. Each parent gets an SMS
            with a one-tap link that pre-binds them to your roster on signup.
            Connect a WhatsApp Business number to blast match cards to the
            whole team in one tap.
          </p>
          <div className="flex flex-wrap gap-2 mt-5 text-xs">
            <Chip tone="#00C9A7">📩 Twilio SMS · 50/coach/day</Chip>
            <Chip tone="#00D9FF">📱 WhatsApp Business · 200/coach/day</Chip>
            <Chip tone="#fbbf24">📊 Invite funnel + delivery analytics built-in</Chip>
            <Chip tone="#00A651">🏏 Drill library + task assignment</Chip>
          </div>
          <div className="flex flex-wrap gap-3 mt-7">
            <button
              onClick={goCoach}
              className="px-5 py-3 rounded-xl font-semibold text-white"
              style={{ background: 'linear-gradient(135deg, #00A651, #00D9FF)' }}
            >
              Set up your roster
            </button>
            <button
              onClick={() => navigate('/drills')}
              className="px-5 py-3 rounded-xl font-semibold"
              style={{
                background: dark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.05)',
                color: dark ? '#f7fafc' : '#0f172a',
              }}
            >
              Browse the drill library →
            </button>
          </div>
        </div>
      </section>

      {/* ===== pricing teaser ===== */}
      <section id="pricing-teaser" className="max-w-6xl mx-auto px-4 py-14 md:py-20">
        <SectionLabel>Pricing</SectionLabel>
        <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight mt-3">
          You pay for insight, not access.
        </h2>
        <p className="opacity-70 max-w-2xl mt-2">
          Every AI action costs a small number of credits. Free starts at 50
          credits/month — enough for ~50 Coach Twin messages or one full video
          analysis. Upgrade for more capacity or top up à la carte.
        </p>

        <div className="grid md:grid-cols-4 gap-4 mt-8">
          {(pricing?.plans || FALLBACK_PLANS).slice(0, 4).map((p) => (
            <PricingMiniCard
              key={p.tier}
              dark={dark}
              tier={p.tier}
              label={p.label}
              price={p.price_usd_cents}
              credits={p.monthly_credits}
              featured={p.tier === 'family_pro'}
            />
          ))}
        </div>

        <div className="mt-6 flex items-center justify-between flex-wrap gap-3">
          <p className="text-sm opacity-70">
            Plus credit packs from $4.99 — never expire.
          </p>
          <button
            onClick={() => navigate('/pricing')}
            className="px-4 py-2 rounded-lg text-sm font-semibold"
            style={{
              background: 'rgba(0,201,167,0.12)',
              color: '#00C9A7',
              border: '1px solid rgba(0,201,167,0.35)',
            }}
          >
            Compare all plans →
          </button>
        </div>
      </section>

      {/* ===== trust strip ===== */}
      <section className="max-w-6xl mx-auto px-4 py-12">
        <div
          className="rounded-2xl px-6 py-6 grid md:grid-cols-4 gap-6 text-center"
          style={{
            background: dark ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.6)',
            border: `1px solid ${dark ? 'rgba(150,160,180,0.15)' : 'rgba(15,23,42,0.08)'}`,
          }}
        >
          <TrustItem dark={dark} icon="🔐" t="Cricket-only AI" b="Every LLM call is topic-classified and credit-debited." />
          <TrustItem dark={dark} icon="📱" t="Phone-first" b="One number signs you in. Email is optional." />
          <TrustItem dark={dark} icon="🧒" t="Built for families" b="Parents own the data. Athletes pick what's private." />
          <TrustItem dark={dark} icon="🌐" t="Open data" b="CricClubs, CricHeroes, str8bat, Stance Beam — one dashboard." />
        </div>
      </section>

      {/* ===== faq ===== */}
      <section className="max-w-3xl mx-auto px-4 py-14 md:py-20">
        <SectionLabel>Questions</SectionLabel>
        <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight mt-3">
          The questions parents actually ask.
        </h2>
        <div className="mt-6 space-y-2">
          {FAQ.map((q) => (
            <FaqItem key={q.q} q={q.q} a={q.a} dark={dark} />
          ))}
        </div>
      </section>

      {/* ===== final CTA ===== */}
      <section className="max-w-4xl mx-auto px-4 py-16 text-center">
        <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight">
          Ready to give your athlete a real coach?
        </h2>
        <p className="opacity-80 mt-3 max-w-2xl mx-auto">
          50 free credits the moment you sign up. No card. Cancel any time —
          there's nothing to cancel.
        </p>
        <button
          onClick={goStart}
          className="mt-7 px-6 py-3.5 rounded-xl font-semibold text-white text-base"
          style={{
            background: 'linear-gradient(135deg, #00A651, #00D9FF)',
            boxShadow: '0 14px 32px -10px rgba(0,201,167,0.55)',
          }}
        >
          Start free — 60-second signup
        </button>
      </section>

      {/* ===== footer ===== */}
      <footer
        className="border-t"
        style={{ borderColor: dark ? 'rgba(150,160,180,0.12)' : 'rgba(15,23,42,0.08)' }}
      >
        <div className="max-w-6xl mx-auto px-4 py-10 grid md:grid-cols-4 gap-6 text-sm">
          <div>
            <Logo size={26} />
            <p className="text-xs opacity-60 mt-3 max-w-xs">
              Cricket development OS for the next generation. Built in California.
            </p>
          </div>
          <FooterCol heading="Product">
            <FooterLink to="/clips" navigate={navigate}>Pro clips</FooterLink>
            <FooterLink to="/practice-plan" navigate={navigate}>Practice plan</FooterLink>
            <FooterLink to="/drills" navigate={navigate}>Drill library</FooterLink>
            <FooterLink to="/pricing" navigate={navigate}>Pricing</FooterLink>
          </FooterCol>
          <FooterCol heading="For coaches">
            <FooterLink to="/coach-dashboard" navigate={navigate}>Coach dashboard</FooterLink>
            <FooterLink to="/onboarding" navigate={navigate}>Roster invite</FooterLink>
          </FooterCol>
          <FooterCol heading="Mobile">
            <AppBadge label="App Store" sub="Coming soon" />
            <AppBadge label="Google Play" sub="Coming soon" />
          </FooterCol>
        </div>
        <div
          className="border-t text-xs opacity-50 py-4 text-center"
          style={{ borderColor: dark ? 'rgba(150,160,180,0.1)' : 'rgba(15,23,42,0.06)' }}
        >
          © {new Date().getFullYear()} Cricket Refreshed
        </div>
      </footer>
    </div>
  );
}

// ---------- subcomponents ----------

function Eyebrow({ children, tone }) {
  return (
    <span
      className="text-[10px] uppercase tracking-widest font-bold inline-flex items-center gap-2"
      style={{ color: tone || '#00C9A7' }}
    >
      <span
        style={{
          width: 6, height: 6, borderRadius: 3,
          background: tone || '#00C9A7',
          boxShadow: `0 0 0 4px ${tone || '#00C9A7'}22`,
        }}
      />
      {children}
    </span>
  );
}

function SectionLabel({ children }) {
  return <Eyebrow>{children}</Eyebrow>;
}

function Chip({ children, tone }) {
  return (
    <span
      className="px-2.5 py-1.5 rounded-full font-semibold"
      style={{
        background: `${tone}22`,
        color: tone,
        border: `1px solid ${tone}55`,
      }}
    >
      {children}
    </span>
  );
}

function PillarCard({
  dark, tone, kicker, title, body, footer, illo, cta, onCta, featured,
}) {
  return (
    <div
      className="rounded-2xl p-6 flex flex-col"
      style={{
        background: featured
          ? `linear-gradient(135deg, ${tone}1f, ${tone}10)`
          : (dark ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.9)'),
        border: `1px solid ${featured ? `${tone}55` : (dark ? 'rgba(150,160,180,0.18)' : 'rgba(15,23,42,0.08)')}`,
      }}
    >
      <div className="mb-3">{illo}</div>
      <Eyebrow tone={tone}>{kicker}</Eyebrow>
      <h3 className="text-xl font-extrabold tracking-tight mt-2">{title}</h3>
      <p className="text-sm opacity-80 mt-2 leading-relaxed flex-1">{body}</p>
      <div className="text-xs opacity-60 mt-4 leading-relaxed">{footer}</div>
      {cta && (
        <button
          onClick={onCta}
          className="text-sm font-semibold mt-4 self-start"
          style={{ color: tone }}
        >
          {cta} →
        </button>
      )}
    </div>
  );
}

function PricingMiniCard({ dark, tier, label, price, credits, featured }) {
  const usd = (price || 0) / 100;
  return (
    <div
      className="rounded-2xl p-5 flex flex-col"
      style={{
        background: featured
          ? 'linear-gradient(135deg, rgba(0,201,167,0.14), rgba(0,217,255,0.10))'
          : (dark ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.9)'),
        border: `1px solid ${featured ? 'rgba(0,201,167,0.4)' : (dark ? 'rgba(150,160,180,0.18)' : 'rgba(15,23,42,0.08)')}`,
      }}
    >
      <div className="text-sm font-bold">{label}</div>
      <div className="text-3xl font-extrabold mt-1">
        {price > 0 ? `$${usd.toFixed(2)}` : 'Free'}
        {price > 0 && <span className="text-xs opacity-60 font-normal"> / mo</span>}
      </div>
      <div className="text-xs opacity-70 mt-1">{credits} credits / month</div>
      {featured && (
        <div className="text-[10px] uppercase tracking-widest font-bold mt-3" style={{ color: '#00C9A7' }}>
          Most popular
        </div>
      )}
    </div>
  );
}

function TrustItem({ dark, icon, t, b }) {
  return (
    <div>
      <div className="text-2xl">{icon}</div>
      <div className="font-bold mt-2">{t}</div>
      <div className="text-xs opacity-70 mt-1 leading-relaxed">{b}</div>
    </div>
  );
}

function FaqItem({ q, a, dark }) {
  const [open, setOpen] = useState(false);
  return (
    <button
      onClick={() => setOpen((v) => !v)}
      className="w-full text-left rounded-xl px-4 py-3.5 transition-colors"
      style={{
        background: open
          ? (dark ? 'rgba(0,201,167,0.07)' : 'rgba(0,201,167,0.05)')
          : (dark ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.9)'),
        border: `1px solid ${open ? 'rgba(0,201,167,0.3)' : (dark ? 'rgba(150,160,180,0.15)' : 'rgba(15,23,42,0.08)')}`,
      }}
    >
      <div className="flex items-center justify-between gap-3">
        <span className="font-semibold">{q}</span>
        <span className="text-sm opacity-60">{open ? '−' : '+'}</span>
      </div>
      {open && <p className="opacity-80 text-sm mt-3 leading-relaxed">{a}</p>}
    </button>
  );
}

function FooterCol({ heading, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest opacity-60 font-bold mb-3">{heading}</div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function FooterLink({ to, navigate, children }) {
  return (
    <button
      onClick={() => navigate(to)}
      className="block opacity-80 hover:opacity-100 text-left"
    >
      {children}
    </button>
  );
}

function AppBadge({ label, sub }) {
  return (
    <div
      className="rounded-lg px-3 py-2 opacity-70"
      style={{
        background: 'rgba(150,160,180,0.08)',
        border: '1px solid rgba(150,160,180,0.2)',
        maxWidth: 160,
      }}
    >
      <div className="text-xs font-bold">{label}</div>
      <div className="text-[10px] opacity-70">{sub}</div>
    </div>
  );
}

// ---------- inline illustrations (no external SVG deps) ----------

function BackgroundOrbs({ dark }) {
  return (
    <>
      <div
        aria-hidden
        style={{
          position: 'absolute', top: -120, right: -100,
          width: 320, height: 320, borderRadius: 320,
          background: dark
            ? 'radial-gradient(circle, rgba(0,201,167,0.25) 0%, transparent 70%)'
            : 'radial-gradient(circle, rgba(0,201,167,0.18) 0%, transparent 70%)',
          filter: 'blur(40px)',
          pointerEvents: 'none',
        }}
      />
      <div
        aria-hidden
        style={{
          position: 'absolute', bottom: -150, left: -100,
          width: 360, height: 360, borderRadius: 360,
          background: dark
            ? 'radial-gradient(circle, rgba(0,217,255,0.18) 0%, transparent 70%)'
            : 'radial-gradient(circle, rgba(0,217,255,0.12) 0%, transparent 70%)',
          filter: 'blur(40px)',
          pointerEvents: 'none',
        }}
      />
    </>
  );
}

function IlloClip({ dark }) {
  return (
    <svg viewBox="0 0 320 160" width="100%" style={{ borderRadius: 12 }}>
      <defs>
        <linearGradient id="il-clip" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#00A651" />
          <stop offset="100%" stopColor="#00D9FF" />
        </linearGradient>
      </defs>
      <rect width="320" height="160" rx="12" fill="#060818" />
      <rect x="14" y="14" width="120" height="80" rx="6" fill="rgba(0,201,167,0.15)" />
      <circle cx="74" cy="54" r="18" fill="rgba(0,201,167,0.9)" />
      <polygon points="68,46 86,54 68,62" fill="#060818" />
      <rect x="14" y="100" width="80" height="6" rx="3" fill="rgba(255,255,255,0.25)" />
      <rect x="14" y="112" width="50" height="6" rx="3" fill="rgba(255,255,255,0.15)" />
      <rect x="148" y="14" width="158" height="20" rx="4" fill="url(#il-clip)" opacity="0.25" />
      <rect x="148" y="38" width="158" height="6" rx="3" fill="rgba(255,255,255,0.25)" />
      <rect x="148" y="48" width="140" height="6" rx="3" fill="rgba(255,255,255,0.2)" />
      <rect x="148" y="58" width="120" height="6" rx="3" fill="rgba(255,255,255,0.15)" />
      <rect x="148" y="74" width="80" height="20" rx="4" fill="url(#il-clip)" />
      <text x="188" y="88" textAnchor="middle" fill="#060818" fontSize="10" fontWeight="800">TRY THIS</text>
      <rect x="14" y="124" width="140" height="6" rx="3" fill="rgba(255,255,255,0.15)" />
      <rect x="14" y="136" width="100" height="6" rx="3" fill="rgba(255,255,255,0.1)" />
    </svg>
  );
}

function IlloChat({ dark }) {
  return (
    <svg viewBox="0 0 320 160" width="100%" style={{ borderRadius: 12 }}>
      <rect width="320" height="160" rx="12" fill="#060818" />
      <rect x="14" y="14" width="200" height="34" rx="17" fill="rgba(0,201,167,0.22)" />
      <text x="24" y="35" fill="#00C9A7" fontSize="11" fontWeight="600">How was my batting this week?</text>
      <rect x="106" y="58" width="200" height="48" rx="14" fill="rgba(0,217,255,0.18)" />
      <text x="116" y="78" fill="#cbd5e1" fontSize="11">Cover-drive timing improved. Try the</text>
      <text x="116" y="92" fill="#cbd5e1" fontSize="11">high-elbow shadow drill tonight before</text>
      <text x="116" y="102" fill="#00C9A7" fontSize="10" fontWeight="700">▍</text>
      <rect x="14" y="120" width="240" height="28" rx="14" fill="rgba(255,255,255,0.06)" />
      <text x="26" y="137" fill="rgba(255,255,255,0.5)" fontSize="10">Ask your coach…</text>
      <rect x="260" y="122" width="44" height="24" rx="6" fill="#00C9A7" />
      <text x="282" y="138" textAnchor="middle" fill="#060818" fontSize="10" fontWeight="800">SEND</text>
    </svg>
  );
}

function IlloFamily({ dark }) {
  return (
    <svg viewBox="0 0 320 160" width="100%" style={{ borderRadius: 12 }}>
      <defs>
        <linearGradient id="il-fam" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#00A651" />
          <stop offset="100%" stopColor="#00D9FF" />
        </linearGradient>
      </defs>
      <rect width="320" height="160" rx="12" fill="#060818" />
      {[
        { x: 16, c: '#00C9A7', s: 6, label: 'A' },
        { x: 84, c: '#00D9FF', s: 5, label: 'V' },
        { x: 152, c: '#fbbf24', s: 4, label: 'M' },
        { x: 220, c: '#A879F0', s: 3, label: 'S' },
      ].map((a) => (
        <g key={a.label}>
          <circle cx={a.x + 24} cy="44" r="20" fill={a.c} opacity="0.85" />
          <text x={a.x + 24} y="48" textAnchor="middle" fontSize="14" fontWeight="800" fill="#060818">
            {a.label}
          </text>
          <rect x={a.x + 8} y="74" width="32" height="5" rx="2.5" fill="rgba(255,255,255,0.35)" />
          <rect x={a.x + 12} y="84" width="24" height="4" rx="2" fill="rgba(255,255,255,0.2)" />
          <text x={a.x + 24} y="106" textAnchor="middle" fill="#fbbf24" fontSize="14">🔥</text>
          <text x={a.x + 24} y="122" textAnchor="middle" fill="rgba(255,255,255,0.6)" fontSize="9" fontWeight="700">
            {a.s}d
          </text>
        </g>
      ))}
      <rect x="14" y="138" width="290" height="12" rx="6" fill="url(#il-fam)" opacity="0.4" />
      <text x="20" y="148" fill="rgba(255,255,255,0.7)" fontSize="9" fontWeight="700">
        Saturday digest · 12 sessions this week
      </text>
    </svg>
  );
}

// ---------- static content ----------

const FAQ = [
  {
    q: 'Is the AI really cricket-only?',
    a: "Yes. Every LLM call passes through an AI Gateway that pre-classifies the message and refuses anything that isn't cricket, athletic performance, fitness, nutrition for cricketers, or mental skills. Off-topic requests don't reach the model and don't bill credits.",
  },
  {
    q: 'Do I need a credit card to start?',
    a: "No. Sign up by phone, get 50 free credits on signup plus 50 more every month. You only pay if you want extra capacity or sign up for a subscription. Cancel any time.",
  },
  {
    q: "What's an 'insight credit'?",
    a: "Credits power AI work. Coach Twin messages cost 1 credit. A 7-day practice plan costs 10. Full video AI analysis costs 25. Match cards cost 2. The full per-feature cost list is on the Pricing page.",
  },
  {
    q: 'Can two parents share one household?',
    a: "Yes — that's the default. The household owner invites the second parent by phone. Both see the same dashboard and athletes. Coaches can be invited the same way.",
  },
  {
    q: 'What happens to my video uploads?',
    a: "Clips upload directly to our S3 bucket via a presigned URL — they never touch our backend storage. The pose overlay runs entirely in your browser; no frames leave the device for that pass. AI analysis (optional) sends only metadata + the S3 key.",
  },
  {
    q: "I'm a coach with parents on WhatsApp already. Useful?",
    a: "Especially. Connect your WhatsApp Business number and you can blast match cards to every parent on your roster in one tap. SMS roster invites are the fastest way to onboard the parents who aren't on the platform yet.",
  },
];

const FALLBACK_PLANS = [
  { tier: 'free',        label: 'Free',        price_usd_cents: 0,    monthly_credits: 50 },
  { tier: 'athlete_pro', label: 'Athlete Pro', price_usd_cents: 999,  monthly_credits: 250 },
  { tier: 'family_pro',  label: 'Family Pro',  price_usd_cents: 1999, monthly_credits: 800 },
  { tier: 'coach',       label: 'Coach',       price_usd_cents: 1499, monthly_credits: 500 },
];
